import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, IconButton, Button, Checkbox, Divider, FormControlLabel, Paper, styled, Switch, Theme, Tooltip, Typography } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

import { makeStyles } from '@mui/styles'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import { AboType, PageAboSettings, Topic } from '@3m5/sm_publish-frontend-api'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import MenuComponent from '@professocial/professocialFormComponents/FormComponents/MenuComponent'
import { DisplayMode } from '@professocial/professocialTypes/types'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import { GetAboTopicHasPostingsRequest } from '@3m5/sm_publish-frontend-api'
import { getAboTopicHasPostingsInitiated } from '../../../store/topic/topic.actions'
import { useDispatch, useSelector } from 'react-redux'
import { getAboHasPostings } from '../../../store/topic/topic.selector'
import { AboHasPostingsObject } from '../../../store/topic/topic.reducer'
import { useRouteMatch } from 'react-router'
import { NavLink } from 'react-router-dom'

interface ITopicCardProps {
  data: Topic
  editable: boolean | undefined
  className?: any
  integrateImages?: boolean
  editMode?: DisplayMode
  editSettings?: (topicId: string | undefined, aboType: AboType) => void
  selectTopic?: (topicID: string) => void
  deleteTopic?: (topicId: string) => void
  aboType?: AboType
  settings?: PageAboSettings
  setAcknowledgeSettings?: (topicId: string, applyToDeferredPosts: boolean) => void
}

const AntSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 18,
    height: 18,
    margin: 1,
  },
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
      },
    },
  },
}))

const TopicCard: React.FC<ITopicCardProps> = (props) => {
  const { t } = useTranslation()
  const editable = props.editable
  const { path } = useRouteMatch()

  const useStyles = makeStyles((theme: Theme) => ({
    card: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      padding: '0px',
      overflow: 'hidden',
    },
    image: {
      width: '100%',
      minHeight: '100%',
      objectFit: 'cover',
      borderTopLeftRadius: '2%',
      borderTopRightRadius: '2%',
    },
    content: {
      padding: theme.spacing(2),
    },
    buttons: {
      padding: theme.spacing(2),
    },
    actionItem: {
      '&.MuiButton-root': {
        textTransform: 'uppercase',
      },
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
    imageWraper: {
      width: '100%',
      minHeight: theme.spacing(19),
      maxHeight: theme.spacing(19),
      overflow: 'hidden',
    },
    standardTopic: {
      paddingRight: theme.spacing(1),
    },
    descriptionBox: {
      margin: theme.spacing(1, 0),
      width: '100%',
      overflow: 'hidden',
      maxHeight: '195px',
    },
    footer: {
      height: '40px',
    },
    info: {
      position: 'absolute',
      top: '10px',
      right: '10px',
    },
  }))
  const classes = useStyles()
  const dispatch = useDispatch()

  const [data, setData] = useState<Topic>(props.data)

  const aboTopicHasPostings: AboHasPostingsObject = useSelector(getAboHasPostings)
  const dispatchGetAboTopicHasPostings = useCallback((params: GetAboTopicHasPostingsRequest) => dispatch(getAboTopicHasPostingsInitiated(params)), [dispatch])

  useEffect(() => {
    if (props.data.id && aboTopicHasPostings[props.data.id] && props.settings?.changedAutomatically) {
      if (aboTopicHasPostings[props.data.id].hasDeferredPosts) {
        setShowChangeDeferredRequest(true)
      } else {
        setShowChangeSettingsRequest(true)
      }
    }
  }, [aboTopicHasPostings])

  useEffect(() => {
    if (props.data.id && props.settings?.changedAutomatically) {
      const params: GetAboTopicHasPostingsRequest = {
        topicId: props.data.id,
      }
      dispatchGetAboTopicHasPostings(params)
    }
  }, [])

  const formatDescription = (description: string) => {
    const product = description.split('\n')
    return (product)
  }
  const [description, setDescription] = useState(formatDescription(String(data.description)))
  const descriptionRef = React.createRef<HTMLDivElement>()
  const [descOverflow, setDescOverflow] = useState(false)

  const [subscribe, setSubscribe] = useState<boolean>(props.aboType === AboType.Automatic || props.aboType === AboType.Individual)
  const [publishType, setPublishType] = useState<boolean>(props.aboType === AboType.Automatic)

  const [showDeleteTopicWarning, setShowDeleteTopicWarning] = useState<boolean>(false)
  const [showChangeDeferredRequest, setShowChangeDeferredRequest] = useState<boolean>(false)
  const [showChangeSettingsRequest, setShowChangeSettingsRequest] = useState<boolean>(false)

  const handleSubscribeChange = (event: React.ChangeEvent<HTMLInputElement> | undefined, checked: boolean) => {
    if (props.editSettings && data) {
      const aboPublishType = publishType ? AboType.Automatic : AboType.Individual
      if (checked) {
        setSubscribe(true)
        props.editSettings(data.id, aboPublishType)
      } else {
        if (props.data.noPostingAllowed) {
          setSubscribe(false)
          setPublishType(false)
          props.editSettings(data.id, AboType.Off)
        }
      }
    }
  }

  const handlePublishTypeChange = (event: React.ChangeEvent<HTMLInputElement> | undefined, checked: boolean) => {
    if (props.editSettings && data) {
      if (checked) {
        if (props.data.automaticPostingAllowed) {
          setPublishType(true)
          props.editSettings(data.id, AboType.Automatic)
        }
      } else {
        if (props.data.manualPostingAllowed) {
          setPublishType(false)
          props.editSettings(data.id, AboType.Individual)
        }
      }
    }
  }

  useEffect(() => {
    setData(props.data)
    setDescription(formatDescription(String(props.data.description)))
  }, [props.data])

  useEffect(() => {
    if (props.aboType) {
      setSubscribe(props.aboType !== AboType.Off)
      setPublishType(props.aboType === AboType.Automatic)
      if (!props.data.manualPostingAllowed) {
        setPublishType(true)
      }
    }
  }, [props.aboType])

  useEffect(() => {
    const div = descriptionRef.current
    if (div && (div.offsetHeight < div.scrollHeight)) {
      setDescOverflow(true)
    } else {
      setDescOverflow(false)
    }
  }, [description, descriptionRef])

  return (
    <>
      <Paper elevation={2} sx={{ width: '100%', position: 'relative' }}>
        {props.settings?.changedAutomatically &&
          <Box className={classes.info}>
            <TooltipComponent
              child={
                <IconButton size='small' sx={{ p: 0 }}>
                  <InfoIcon />
                </IconButton>
              }
              title={`${t('topicsCommon:changingInfo')}`}
            />
          </Box>}
        <Box className={classes.card}>
          {props.integrateImages && (
            <Box className={classes.imageWraper} display='flex' justifyContent='center'>
              <Box sx={{ display: 'contents' }}>
                <ImageComponent
                  className={classes.image}
                  src={data.pictureUrl}
                  fallback='/src/shared/assets/images/icon-img-placeholder.svg'
                />
              </Box>
            </Box>
          )}
          <Box className={classes.content}>
            <Box display='flex' flexWrap='wrap' alignItems='center'>
              <Typography><b>{data.name}</b></Typography>
            </Box>
            <Box>
              <div className={classes.descriptionBox} ref={descriptionRef} id={String(description)}>
                {description.map((text: string, i: number) => (
                  <Box key={i} flexGrow={1}>
                    <Typography variant='body1' style={{ wordWrap: 'break-word', whiteSpace: 'break-spaces' }}>{text}&nbsp;</Typography>
                  </Box>
                ))}
              </div>
              <Box display='flex' visibility={descOverflow ? 'visible' : 'hidden'}>
                <Box flexGrow={1}>&nbsp;</Box>
                <Box alignSelf='flex-end'>
                  <Tooltip
                    title={description.map((text: string, i: number) => {
                      return (
                        <Box key={i} flexGrow={1}>
                          <Typography variant='body1'>{text}&nbsp;</Typography>
                        </Box>
                      )
                    })}
                  >
                    <Button
                      variant='text' style={{
                        maxWidth: '10px',
                        maxHeight: '10px',
                        minWidth: '10px',
                        minHeight: '10px',
                      }}
                    ><MoreHorizIcon color='info' />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            <Box> {/* TODO Aktualisieren nach dem die Statistiken da sind */}
              <Typography>
                {/* {`${t('topicsCommon:individualReleases')}: ${data.topic.statistics && (data.topic.statistics.find((stats: TopicStatistic) => stats.aboCount}`} */}
              </Typography>
            </Box>
            <Box>
              <Typography>
                {/* {`${t('topicsCommon:automaticReleases')}: ${data.topic.statistics && (data.topic.statistics.find((stats: TopicStatistic) => stats.estimatedReach}`} */}
              </Typography>
            </Box>
          </Box>
          {editable && (
            <>{
              props.editMode === DisplayMode.newScreen ? (
                <Box sx={{ marginTop: 'auto' }}>
                  <Divider />
                  <Box display='flex' flexWrap='wrap' className={classes.buttons}>
                    <Box>
                      <NavLink to={`${path}/edit/${data.id}`}>
                        <ButtonComponent
                          className={classes.actionItem}
                          variant='text'
                          size='large'
                          color='info'
                          disableElevation text={t('topicsCommon:edit')}
                        />
                      </NavLink>
                    </Box>
                    <Box>
                      <ButtonComponent
                        className={classes.actionItem}
                        size='large'
                        color='info'
                        variant='text'
                        disableElevation
                        text={t('topicsCommon:delete')}
                        onClick={() => setShowDeleteTopicWarning(true)}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ py: 1, display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                  <MenuComponent
                    className='primary'
                    menuItem={[
                      {
                        label: t('topicsCommon:edit'),
                        value: 'edit',
                        handleClick: () => props.selectTopic && data.id && props.selectTopic(data.id),
                      },
                      {
                        label: t('topicsCommon:delete'),
                        value: 'delete',
                        handleClick: () => setShowDeleteTopicWarning(true),
                      },
                    ]}
                  />
                </Box>
              )
            }
            </>
          )}
          {!editable && (
            <Box sx={{ p: 2, pt: 0, marginTop: 'auto' }}>
              <FormControlLabel
                control={
                  <AntSwitch
                    disabled={(!props.data.automaticPostingAllowed && !props.data.manualPostingAllowed) || (!props.data.noPostingAllowed)}
                    id='subscribe'
                    name='subscribe'
                    color='primary'
                    checked={subscribe}
                    onChange={handleSubscribeChange}
                  />
                }
                label={subscribe ? t('topicsCommon:subscribed') as string : t('topicsCommon:subscribe') as string}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    id='publishType'
                    name='publishType'
                    checked={publishType}
                    disabled={!subscribe || !props.data.manualPostingAllowed || !props.data.automaticPostingAllowed}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                    onChange={handlePublishTypeChange}
                  />
                }
                label={<Box>{t('topicsCommon:setOnline')}</Box>}
              />
            </Box>
          )}
        </Box>
        <DialogComponent
          type='error'
          title={t('topicsMessages:deleteWarningTitle')}
          message={t('topicsMessages:deleteTopicWarning')}
          open={showDeleteTopicWarning}
          confirmLabel={t('topicsCommon:delete')}
          cancelLabel={t('topicsCommon:cancel')}
          autoClose={false}
          requestActions
          onClose={() => setShowDeleteTopicWarning(false)}
          onCancel={() => setShowDeleteTopicWarning(false)}
          onConfirm={() => {
            setShowDeleteTopicWarning(false)
            props.deleteTopic && data.id && props.deleteTopic(data.id)
          }}
        />
        <DialogComponent
          type='error'
          title={t('topicsMessages:editSettingsRequestTitle')}
          message={t('topicsMessages:changeDeferredSettingsRequest')}
          open={showChangeDeferredRequest}
          confirmLabel={t('topicsMessages:requestYes')}
          cancelLabel={t('topicsMessages:requestNo')}
          autoClose={false}
          requestActions
          onClose={() => setShowChangeDeferredRequest(false)}
          onCancel={() => {
            setShowChangeDeferredRequest(false)
            props.data.id && props.setAcknowledgeSettings && props.setAcknowledgeSettings(props.data.id, false)
          }}
          onConfirm={() => {
            setShowChangeDeferredRequest(false)
            props.data.id && props.setAcknowledgeSettings && props.setAcknowledgeSettings(props.data.id, true)
          }}
        />
        <DialogComponent
          type='error'
          title={t('topicsMessages:editSettingsRequestTitle')}
          message={t('topicsMessages:changeSettingsRequest')}
          open={showChangeSettingsRequest}
          confirmLabel={t('topicsCommon:ok')}
          cancelLabel={null}
          autoClose={false}
          requestActions
          onClose={() => setShowChangeSettingsRequest(false)}
          onConfirm={() => {
            setShowChangeSettingsRequest(false)
            props.data.id && props.setAcknowledgeSettings && props.setAcknowledgeSettings(props.data.id, false)
          }}
        />
      </Paper>
    </>
  )
}

export default TopicCard
